import { useTranslation } from "react-i18next";
import {
  Grid,
  Button,
  Skeleton,
  CircularProgress,
  TextField,
  FormHelperText,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { NoInfoComponent } from "../../components/common/NoInfoImage/noInfoComponent";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

import { FilterDynamicReport } from "../../components/common/infonavit/filterDynamicReport";
import ModalInfo from "../../components/common/Modals/modalInfo";
import CustomTablePagination from "../../components/DataTable/CustomTablePagination";
import { getUser, getUserStatus } from "../userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../consts/codes";
import { avisosFields } from "../../consts/avisosFields";

import {
  GetDynamicReportConf,
  CreateDynamicReportConf,
  UpdateDynamicReportConf,
} from "../../services/user/infonavit/adminDynamicReport";
import { FilterUsersClient } from "../../services/user/consultRolesService";
import jwt_decode from "jwt-decode";
import { REGEXP } from "../../consts/regexp";

/**
 * Const
 */
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const DynamicReport = () => {
  /**
   * Tomando el rol por el token
   */
  const jwtTokenInfo = localStorage.getItem("infoToken");

  let role = "";
  if (jwtTokenInfo) {
    const jwtDecodeTokenInfo = jwt_decode(jwtTokenInfo);

    role = jwtDecodeTokenInfo["custom:Role"];
  }

  /**
   * UseState
   */
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [confData, setConfData] = useState(null);
  const [emailList, setEmailList] = useState([]);

  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [saveData, setSaveData] = useState({});

  const [loadingSave, setLoadingSave] = useState(false);

  /**
   * UseTranslation
   */
  const { t } = useTranslation();

  const selectAllItem = {
    label: t("App.infonavitDynamicReport.filterFields.selectAll"),
    value: t("App.infonavitDynamicReport.filterFields.selectAll"),
  };

  /**
   * Schema
   */
  const schema = yup.object().shape({
    txtSeparator: yup.string().required(t("App.validationMessages.required")),
    emailList: yup
      .array()
      .min(1, t("App.validationMessages.required"))
      .max(
        4,
        t("App.infonavitDynamicReport.validationMessages.maxEmails", {
          number: 3,
        })
      ),
    reportFields: yup
      .array()
      .test(
        "at-least-one",
        t("App.validationMessages.atLeastOne"),
        (value, context) => value.findIndex((item) => item.enabled) !== -1
      ),
    daysFrequency: yup
      .string()
      .max(3, t("App.validationMessages.maxCharacters", { number: 3 }))
      .required(t("App.validationMessages.required"))
      .matches(REGEXP.ONLY_NUMBERS, t("App.validationMessages.onlyNumber")),
    reports: yup.object().shape(
      {
        excelReport: yup.boolean().when(["txtReport"], {
          is: (txtReport) => txtReport === false,
          then: (schema) =>
            schema.oneOf([true], t("App.validationMessages.atLeastOne")),
          otherwise: (schema) => schema,
        }),
        txtReport: yup.boolean().when(["excelReport"], {
          is: (excelReport) => excelReport === false,
          then: (schema) =>
            schema.oneOf([true], t("App.validationMessages.atLeastOne")),
          otherwise: (schema) => schema,
        }),
      },
      [["excelReport", "txtReport"]]
    ),
  });

  /**
   * useForm
   */
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      txtSeparator: "",
      emailList: [],
      daysFrequency: "",
      reports: { txtReport: false, excelReport: false },
      reportFields: [],
    },
  });

  const reportFieldsReactive = watch("reportFields");

  const columns = [
    {
      name: "order",
      label: t("App.infonavitDynamicReport.table.order"),
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => {
          return (obj1, obj2) => {
            return (
              (obj1.data - obj2.data ? 1 : -1) * (order === "asc" ? 1 : -1)
            );
          };
        },
        customBodyRenderLite: (dataIndex, rowIndex) => {
          return (
            <Controller
              name={`reportFields.${dataIndex}.order`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  type="text"
                  value={getValues(`reportFields.${dataIndex}.order`)}
                  onChange={(event) => {
                    let value = event.target.value;

                    // Si se deja vacío el input, se cambia su valor pero no se
                    // ejecuta la lógica de ordenamiento para mantener todo igual
                    // hasta que el usuario digite algo.
                    if (value === "") return onChange(value);
                    if (isNaN(value)) {
                      value = "";
                    }
                    onChange(value);

                    // Se establece el nuevo orden de los inputs:
                    // * Si alguno de los elementos tiene order === "", significa que
                    //   se ingresó un valor NO numérico y por lo tanto se debe mantener
                    //   el ordenamiento entre dichos elementos.
                    // * De lo contrario, se hace la resta para saber cuál debe ir antes
                    //   y se resta 1 al resultado para que al presentarse dos elementos
                    //   con el mismo orden, el que estaba debajo, suba.
                    const newReportFields = getValues("reportFields").sort(
                      (a, b) => {
                        if (a === "" || b === "") return 0;
                        return a.order - b.order - 1;
                      }
                    );

                    // Ya organizados los elementos, se actualiza su campo "order" para
                    // coincida con su index.
                    setValue(
                      "reportFields",
                      newReportFields.map((field, index) => {
                        return { ...field, index, order: index + 1 };
                      })
                    );

                    // Al terminar de organizar nuevamente los inputs, se quita el focus
                    // del input
                    event.target.blur();
                  }}
                  helperText={
                    errors?.reportFields?.[dataIndex]
                      ? errors?.reportFields[dataIndex].message
                      : ""
                  }
                  inputProps={{
                    style: {
                      margin: "-5px 0 0 0 !important",
                      fontSize: 12,
                      border: "1px solid !important",
                    },
                    maxLength: "1",
                  }}
                  className={
                    errors.status
                      ? "select__filters__one-digit select__filters__errors"
                      : "select__filters__one-digit"
                  }
                />
              )}
            />
          );
        },
      },
    },
    {
      name: "label",
      label: t("App.infonavitDynamicReport.table.name"),
      options: {
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "enabled",
      label: t("App.infonavitDynamicReport.table.enabled"),
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => {
          return (obj1, obj2) => {
            return (
              (!obj1.data && obj2.data ? 1 : -1) * (order === "asc" ? 1 : -1)
            );
          };
        },
        customBodyRenderLite: (dataIndex, rowIndex) => {
          return (
            <Controller
              name={`reportFields.${dataIndex}.enabled`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={getValues(`reportFields.${dataIndex}.enabled`)}
                  onChange={onChange}
                  index={dataIndex}
                />
              )}
            />
          );
        },
      },
    },
  ];

  const options = {
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    viewColumns: false,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  /**
   * Use Effect
   */
  /**
   * Carga la configuración
   */
  useEffect(() => {
    if (userStatus === "succeeded") fetchConfiguration();
  }, [userStatus]);

  /**
   * Function to get the list of emails and set the data in state of emailList
   */
  async function handleEmailList() {
    try {
      const eanCompany = userInfo?.company?.eanCompany;

      const userService = await FilterUsersClient({ eanCompany: eanCompany });

      const { responseCode, responseMessage } = userService.data;

      if (responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST) {
        const userEmailList = responseMessage.map((user) => {
          return {
            label: user?.email,
            value: user?.email,
          };
        });
        const preselectedEmails = getValues("emailList");

        const notListedEmails = preselectedEmails.filter(
          (item) => !userEmailList.find((aux) => aux.value === item.value)
        );

        const newList = [...userEmailList, ...notListedEmails];

        // Add select all option
        setEmailList([selectAllItem, ...newList]);

        if (newList.length === preselectedEmails.length) {
          setValue("emailList", [selectAllItem, ...preselectedEmails]);
        }
      } else {
        setEmailList([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  }

  /**
   * Función que consulta y settea la información de los usuarios
   */
  const fetchConfiguration = async () => {
    try {
      const mainUser = userInfo.mainUser;

      setIsLoadingData(true);

      const {
        status,
        data: { responseStatus, response },
      } = await GetDynamicReportConf(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        if (response !== null) {
          const tempEmailList = response?.emailList?.map((email) => {
            return {
              value: email,
              label: email,
            };
          });

          setValue("txtSeparator", response.txtReportDelimiter ?? "");
          setValue("daysFrequency", response.reportingFrequency ?? "");
          setValue("reports.excelReport", response.excelReport ?? false);
          setValue("reports.txtReport", response.txtReport ?? false);
          setValue("emailList", tempEmailList ?? []);

          handleEmailList();
        }

        const originalReportFields = response?.reportFields ?? [];

        let rpFields = avisosFields.filter(
          (field) => !originalReportFields.includes(field.name)
        );

        rpFields = originalReportFields
          .map((a) => {
            return avisosFields.find((b) => a === b.name);
          })
          .concat(rpFields);

        rpFields = rpFields.map((field, index) => {
          const enabled = originalReportFields.includes(field.name);
          return { index, order: index + 1, enabled, ...field };
        });

        setValue("reportFields", rpFields);
        setConfData(response);
      } else {
        setConfData(null);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      setConfData(null);
      console.log("====================================");
    }
  };

  /**
   * Función que retorna el título del modal según código de respuesta
   * @param {Number} responseCode código de respuesta
   * @returns título según código
   */
  const handleModalTitle = (responseCode) => {
    if (responseCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST) {
      return "";
    } else if (responseCode === 1001) {
      return t("App.userDetail.modalTitle.title2");
    } else if (responseCode === 1002) {
      return t("App.userDetail.modalTitle.title1");
    } else {
      return "";
    }
  };

  /**
   *
   * Function to edit dynamic report configuration
   *
   * @param {Object} payload
   */
  const saveChanges = async (payload) => {
    try {
      setLoadingSave(true);

      const saveFunction =
        confData === null ? CreateDynamicReportConf : UpdateDynamicReportConf;

      const {
        status,
        data: { responseStatus, response },
      } = await saveFunction(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        fetchConfiguration();
      }

      setModalTitle(handleModalTitle(responseStatus.statusCode));
      setOpenModalInfo(true);
      setSaveData({ data: { responseStatus, response }, status });

      setLoadingSave(false);
    } catch (error) {
      setLoadingSave(false);
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Función para cancelar
   */
  const handleClose = async () => {
    setLoadingSave(true);

    await fetchConfiguration();
    reset();

    setLoadingSave(false);
  };

  /**
   *
   * Function to save changes
   *
   * @param {Object} data
   */
  const handleSaveChanges = async (data) => {
    const tempEmailList = data.emailList.filter(
      (item) =>
        item.label !== t("App.infonavitDynamicReport.filterFields.selectAll")
    );
    const tempReportFields = data.reportFields.filter((item) => item.enabled);

    let payload = {
      mainUser: userInfo.mainUser,
      reportingFrequency: parseInt(data.daysFrequency),
      emailList: tempEmailList.map((item) => item.value),
      excelReport: data.reports.excelReport,
      txtReport: data.reports.txtReport,
      txtReportDelimiter: data.txtSeparator,
      reportFields: tempReportFields.map((item) => item.name),
    };
    if (confData !== null) payload = { ...confData, ...payload };

    return saveChanges(payload);
  };

  return (
    <form id="dynamic-report" onSubmit={handleSubmit(handleSaveChanges)}>
      <Grid
        container
        rowGap="2rem"
        paddingBottom="2rem !important"
        padding={{
          xs: "4.5rem 2rem",
          sm: "4.5rem 3rem",
          md: "4.5rem 3rem",
          lg: "4.5rem",
          xl: "4.5rem",
        }}
      >
        <Grid item xs={12}>
          <p className="display-large__primary-one">
            {t("App.infonavitDynamicReport.title")}
          </p>
        </Grid>

        <Grid item xs={12} className="companies_info_container">
          <FilterDynamicReport
            formState={formState}
            control={control}
            emailList={emailList}
            setEmailList={setEmailList}
            getValues={getValues}
            setValue={setValue}
            selectAllItem={selectAllItem}
          />

          <Grid
            container
            alignItems="center"
            justifyContent="flex-start"
            gap={{ xs: 0, sm: 3 }}
            marginTop="2rem"
          >
            <Controller
              name="reports.txtReport"
              control={control}
              render={({ field: { onChange, value } }) => (
                <p style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={value}
                    onChange={onChange}
                  />
                  {t("App.infonavitDynamicReport.filterFields.txtReport")}
                </p>
              )}
            />

            <Controller
              name="reports.excelReport"
              control={control}
              render={({ field: { onChange, value } }) => (
                <p style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={value}
                    onChange={onChange}
                  />

                  {t("App.infonavitDynamicReport.filterFields.excelReport")}
                </p>
              )}
            />
          </Grid>

          <FormHelperText error={true} style={{ marginLeft: "1rem" }}>
            {errors?.reports?.excelReport || errors?.reports?.txtReport
              ? t("App.validationMessages.atLeastOne")
              : ""}
          </FormHelperText>
        </Grid>

        <Grid item xs={12}>
          <Grid container gap={2} className="companies_info_container">
            <Grid item xs={12}>
              <Grid container justifyContent="flex-start" gap={1}>
                <ManageAccountsIcon className="accordion__upload__consultIcon" />

                <p
                  className="display-small__primary-one"
                  style={{ paddingLeft: "1rem" }}
                >
                  {t("App.infonavitDynamicReport.table.title")}
                </p>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormHelperText error={true} style={{ marginLeft: "1rem" }}>
                {errors?.reportFields ? errors.reportFields.message : ""}
              </FormHelperText>

              <MUIDataTable
                className={`dataTable__salesForce datatable-style ${
                  isLoadingData || reportFieldsReactive.length < 1
                    ? ""
                    : "dynamic-report-table"
                }`}
                style={{ marginTop: "5%", display: "none" }}
                components={{
                  TableToolbar(props) {
                    return null;
                  },
                  TableFooter: (props) => (
                    <CustomTablePagination paginationProps={props} />
                  ),
                }}
                data={reportFieldsReactive}
                columns={columns}
                options={options}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container justifyContent="center" gap={2}>
                <Button
                  className="btn__applyFilter"
                  type="submit"
                  form="dynamic-report"
                >
                  {!loadingSave ? (
                    t("App.buttonLabels.save")
                  ) : (
                    <CircularProgress size={21} color="inherit" />
                  )}
                </Button>

                <Button className="btn__filled__gradient" onClick={handleClose}>
                  {t("App.buttonLabels.cancel")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ModalInfo
        title={modalTitle}
        responseData={saveData}
        onClose={() => {
          setOpenModalInfo(false);
        }}
        open={openModalInfo}
      />
    </form>
  );
};

export default DynamicReport;
